import { useState, useEffect, useRef } from "react";
import { DatePicker, Select, Button, Table, Spin, Empty } from "antd";
import {
  CheckCircleOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  FieldTimeOutlined,
  HourglassOutlined,
  FileExcelFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import { Bar, Chart } from "react-chartjs-2";
import dayjs from "dayjs";
import SummaryCard from "./SummaryCard";
import ChartCard from "./ChartCard";
import { minutesToDisplay } from "../utils/timeUtils";
import {
  COMPLEX_OPTIONS,
  disabledDate,
  roundCount,
  roundPercent,
  TOOLTIPS,
  resetChartLegend,
  buildDeliveryCountData,
  buildDeliveryCountOptions,
  buildStackedBarData,
  buildStackedBarOptions,
  buildDetailTimeData,
  buildDetailTimeOptions,
  buildHStackedBarData,
  buildHStackedBarOptions,
  buildDestCountData,
  buildDestCountOptions,
  buildHDetailTimeData,
  buildHDetailTimeOptions,
  buildSimpleBarData,
  buildSimpleBarOptions,
  LEGEND_DELIVERY_COUNT,
  LEGEND_STACKED_TIME,
  LEGEND_DEST_COUNT,
  LEGEND_HSTACKED_TIME,
  LEGEND_DETAIL_TIME,
} from "../utils/dashboardUtils";
import {
  getDeviceStats,
  getDeviceDaily,
  getDeviceDow,
  getDeviceHourly,
  getDeviceDestination,
  downloadRawDataExcel,
} from "../services/dashboardApi";

const { RangePicker } = DatePicker;

const DEFAULT_RANGE = [dayjs().subtract(30, "day"), dayjs().subtract(1, "day")];

// ── 목적지 테이블 컬럼 ─────────────────────────────────────────
const countTableCols = [
  { title: "목적지명", dataIndex: "destination", key: "destination" },
  {
    title: "이동 시도 횟수",
    dataIndex: "moveCount",
    key: "moveCount",
    align: "right",
    render: (v) => `${v}회`,
  },
  {
    title: "이동 성공률 (%)",
    dataIndex: "successRate",
    key: "successRate",
    align: "right",
    render: (v) => (v != null ? `${roundPercent(v)}%` : "-"),
  },
];

const totalTimeTableCols = [
  { title: "목적지명", dataIndex: "destination", key: "destination" },
  {
    title: "운행 시간",
    dataIndex: "totalDriveMinutes",
    key: "totalDriveMinutes",
    align: "right",
    render: (v) => minutesToDisplay(v),
  },
  {
    title: "대기 시간",
    dataIndex: "totalWaitMinutes",
    key: "totalWaitMinutes",
    align: "right",
    render: (v) => minutesToDisplay(v),
  },
];

const avgTimeTableCols = [
  { title: "목적지명", dataIndex: "destination", key: "destination" },
  {
    title: "평균 운행 시간",
    dataIndex: "avgDriveMinutes",
    key: "avgDriveMinutes",
    align: "right",
    render: (v) => (v == null ? "-" : `${minutesToDisplay(v)}/회`),
  },
  {
    title: "운행 최대·최소",
    key: "driveRange",
    align: "right",
    render: (_, r) =>
      `${minutesToDisplay(r.maxDriveMinutes)}, ${minutesToDisplay(r.minDriveMinutes)}`,
  },
  {
    title: "평균 대기 시간",
    dataIndex: "avgWaitMinutes",
    key: "avgWaitMinutes",
    align: "right",
    render: (v) => (v == null ? "-" : `${minutesToDisplay(v)}/회`),
  },
  {
    title: "대기 최대·최소",
    key: "waitRange",
    align: "right",
    render: (_, r) =>
      `${minutesToDisplay(r.maxWaitMinutes)}, ${minutesToDisplay(r.minWaitMinutes)}`,
  },
];

// ── 목적지별 이동 횟수 (단지 1개) ─────────────────────────────
const LEGEND_ROW_H = 36;
const TABLE_HEADER_H = 37;

const EMPTY_DESC = (
  <span>
    현재 조건에 해당하는 데이터가 없어
    <br />
    데이터가 표시되지 않습니다.
  </span>
);

const EmptyChart = ({ height }) => (
  <div
    style={{
      height: height ?? "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_DESC} />
  </div>
);

const DestCountItem = ({ deviceName, data, showTitle }) => {
  const ref = useRef(null);
  const chartH = 250;
  const tableScrollY = chartH + LEGEND_ROW_H - TABLE_HEADER_H;
  if (data.countChart.labels.length === 0) {
    return (
      <div className="dest-item">
        {showTitle && <div className="dest-complex-title">{deviceName}</div>}
        <EmptyChart height={chartH} />
      </div>
    );
  }
  return (
    <div className="dest-item">
      {showTitle && <div className="dest-complex-title">{deviceName}</div>}
      <div className="destination-section">
        <div className="dest-chart-col">
          <div className="destination-chart-wrapper" style={{ height: chartH }}>
            <Chart
              ref={ref}
              type="bar"
              data={buildDestCountData(
                data.countChart.labels,
                data.countChart.moveCounts,
                data.countChart.successRates,
              )}
              options={buildDestCountOptions()}
            />
          </div>
          <div className="chart-legend-row">
            <div className="chart-legend-items">
              {LEGEND_DEST_COUNT.map((item, i) => (
                <span key={i} className="chart-legend-item">
                  <span
                    className="chart-legend-box"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
            <button
              className="chart-reset-btn"
              onClick={() => resetChartLegend(ref)}
            >
              <ReloadOutlined
                style={{ fontSize: 16, color: "rgba(0,0,0,0.88)" }}
              />
              선택 초기화
            </button>
          </div>
        </div>
        <div className="destination-table-wrapper">
          <Table
            columns={countTableCols}
            dataSource={data.countTable.map((r, i) => ({ key: i, ...r }))}
            pagination={false}
            size="small"
            className="destination-table"
            scroll={{ y: tableScrollY }}
          />
        </div>
      </div>
    </div>
  );
};

// ── 목적지별 배송 시간 (단지 1개) ─────────────────────────────
const DestTotalTimeItem = ({ deviceName, data, showTitle }) => {
  const ref = useRef(null);
  const chartH = Math.max(180, data.totalTimeChart.labels.length * 36 + 60);
  const tableScrollY = chartH + LEGEND_ROW_H - TABLE_HEADER_H;
  if (data.totalTimeChart.labels.length === 0) {
    return (
      <div className="dest-item">
        {showTitle && <div className="dest-complex-title">{deviceName}</div>}
        <EmptyChart height={180} />
      </div>
    );
  }
  return (
    <div className="dest-item">
      {showTitle && <div className="dest-complex-title">{deviceName}</div>}
      <div className="destination-section">
        <div className="dest-chart-col">
          <div className="destination-chart-wrapper" style={{ height: chartH }}>
            <Bar
              ref={ref}
              data={buildHStackedBarData(
                data.totalTimeChart.labels,
                data.totalTimeChart.driveTotals,
                data.totalTimeChart.waitTotals,
              )}
              options={buildHStackedBarOptions()}
            />
          </div>
          <div className="chart-legend-row">
            <div className="chart-legend-items">
              {LEGEND_HSTACKED_TIME.map((item, i) => (
                <span key={i} className="chart-legend-item">
                  <span
                    className="chart-legend-box"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
            <button
              className="chart-reset-btn"
              onClick={() => resetChartLegend(ref)}
            >
              <ReloadOutlined
                style={{ fontSize: 16, color: "rgba(0,0,0,0.88)" }}
              />
              선택 초기화
            </button>
          </div>
        </div>
        <div className="destination-table-wrapper">
          <Table
            columns={totalTimeTableCols}
            dataSource={data.totalTimeTable.map((r, i) => ({ key: i, ...r }))}
            pagination={false}
            size="small"
            className="destination-table"
            scroll={{ y: tableScrollY }}
          />
        </div>
      </div>
    </div>
  );
};

// ── 목적지별 배송 시간 상세 (단지 1개) ───────────────────────
const DestAvgTimeItem = ({ deviceName, data, showTitle }) => {
  const ref = useRef(null);
  const chartH = Math.max(200, data.avgTimeChart.labels.length * 60 + 60);
  const tableScrollY = chartH + LEGEND_ROW_H - TABLE_HEADER_H;

  if (data.avgTimeChart.labels.length === 0) {
    return (
      <div className="dest-item">
        {showTitle && <div className="dest-complex-title">{deviceName}</div>}
        <EmptyChart height={200} />
      </div>
    );
  }
  return (
    <div className="dest-item">
      {showTitle && <div className="dest-complex-title">{deviceName}</div>}
      <div className="destination-section">
        <div className="dest-chart-col">
          <div className="destination-chart-wrapper" style={{ height: chartH }}>
            <Chart
              ref={ref}
              type="bar"
              data={buildHDetailTimeData(
                data.avgTimeChart.labels,
                data.avgTimeChart.driveAvgs,
                data.avgTimeChart.driveMaxs,
                data.avgTimeChart.driveMins,
                data.avgTimeChart.waitAvgs,
                data.avgTimeChart.waitMaxs,
                data.avgTimeChart.waitMins,
              )}
              options={buildHDetailTimeOptions()}
            />
          </div>
          <div className="chart-legend-row">
            <div className="chart-legend-items">
              {LEGEND_DETAIL_TIME.map((item, i) => (
                <span key={i} className="chart-legend-item">
                  <span
                    className="chart-legend-box"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="destination-table-wrapper">
          <Table
            columns={avgTimeTableCols}
            dataSource={data.avgTimeTable.map((r, i) => ({ key: i, ...r }))}
            pagination={false}
            size="small"
            className="destination-table"
            scroll={{ y: tableScrollY }}
          />
        </div>
      </div>
    </div>
  );
};

// ── 메인 컴포넌트 ──────────────────────────────────────────────
const DashboardIndividual = () => {
  const [deviceId, setDeviceId] = useState("");
  const [dateRange, setDateRange] = useState(DEFAULT_RANGE);
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [dow, setDow] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [destination, setDest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownload] = useState(false);

  // 차트 ref (선택 초기화용)
  const dailyDeliveryRef = useRef(null);
  const dailyTimeRef = useRef(null);
  const dailyDetailRef = useRef(null);
  const dowDeliveryRef = useRef(null);
  const dowTimeRef = useRef(null);
  const dowDetailRef = useRef(null);

  useEffect(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    const params = {
      startYmd: dateRange[0].format("YYYY-MM-DD"),
      endYmd: dateRange[1].format("YYYY-MM-DD"),
      deviceId: deviceId || undefined,
    };
    let cancelled = false;
    setStats(null);
    setDaily(null);
    setDow(null);
    setHourly(null);
    setDest(null);
    setLoading(true);
    Promise.all([
      getDeviceStats(params),
      getDeviceDaily(params),
      getDeviceDow(params),
      getDeviceHourly(params),
      getDeviceDestination(params),
    ])
      .then(([s, d, w, h, dest]) => {
        if (cancelled) return;
        setStats(s);
        setDaily(d);
        setDow(w);
        setHourly(h);
        setDest(dest);
      })
      .catch((err) => {
        if (!cancelled) console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateRange, deviceId]);

  const handleDownload = async () => {
    setDownload(true);
    try {
      const params = {
        startYmd: dateRange[0].format("YYYY-MM-DD"),
        endYmd: dateRange[1].format("YYYY-MM-DD"),
        deviceId: deviceId || undefined,
      };
      const res = await downloadRawDataExcel(params);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rawData_${params.startYmd}_${params.endYmd}${deviceId ? `_${deviceId}` : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownload(false);
    }
  };

  const isAllComplex = deviceId === "";

  return (
    <div className="dashboard-tab-content">
      {/* ── 필터 ── */}
      <div className="dashboard-filter-row">
        <div className="dashboard-filter-left">
          <div className="filter-group">
            <label>조회 단지</label>
            <Select
              value={deviceId}
              onChange={setDeviceId}
              options={COMPLEX_OPTIONS}
              style={{ width: 240 }}
            />
          </div>
          <div className="filter-group">
            <label>조회 기간</label>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY.MM.DD"
              disabledDate={disabledDate}
              placeholder={["YYYY.MM.DD", "YYYY.MM.DD"]}
            />
          </div>
          <p className="filter-note">
            조회 기간은 현재 기준 6개월 이내 데이터 중 최대 30일 범위로 설정할
            수 있습니다.
          </p>
        </div>
        <Button
          icon={<FileExcelFilled />}
          onClick={handleDownload}
          loading={downloading}
        >
          원본데이터 다운로드
        </Button>
      </div>

      <Spin spinning={loading}>
        {/* ── 요약 ── */}
        <div className="kpi-section">
          <SummaryCard
            icon={
              <CheckCircleOutlined
                style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
              />
            }
            label="유효 배송 횟수"
            tooltip={TOOLTIPS.validDelivery}
            mainValue={
              stats?.validDeliveryCount == null ? (
                "-"
              ) : (
                <>
                  <span>
                    {roundCount(stats?.validDeliveryCount).toFixed(2)}
                  </span>
                  <span style={{ fontSize: 18 }}>회</span>
                </>
              )
            }
            subText={
              stats?.attemptCount != null
                ? `총 ${stats.attemptCount}회 배송 시도`
                : null
            }
          />
          <SummaryCard
            icon={
              <EnvironmentOutlined
                style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
              />
            }
            label="배송 목적지 도달률"
            tooltip={TOOLTIPS.arrivalRate}
            mainValue={
              roundPercent(stats?.deliveryArrivalRate) == null ? (
                "-"
              ) : (
                <>
                  <span>{roundPercent(stats.deliveryArrivalRate)}</span>
                  <span style={{ fontSize: 18 }}>%</span>
                </>
              )
            }
          />
          <SummaryCard
            icon={
              <HistoryOutlined
                style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
              />
            }
            label="배송 시간"
            tooltip={TOOLTIPS.cycleTime}
            mainValue={minutesToDisplay(stats?.totalCycleDurationMinutes)}
            subText={
              !isAllComplex && stats?.avgCycleDurationMinutes
                ? `배송 1회당 평균 ${minutesToDisplay(stats.avgCycleDurationMinutes)} 소요`
                : null
            }
          />
          <SummaryCard
            icon={
              <FieldTimeOutlined
                style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
              />
            }
            label="운행 시간"
            tooltip={TOOLTIPS.driveTime}
            mainValue={minutesToDisplay(stats?.totalDriveMinutes)}
            subText={
              !isAllComplex && stats?.avgDriveMinutes
                ? `배송 1회당 평균 ${minutesToDisplay(stats.avgDriveMinutes)} 소요`
                : null
            }
          />
          <SummaryCard
            icon={
              <HourglassOutlined
                style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
              />
            }
            label="대기 시간"
            tooltip={TOOLTIPS.waitTime}
            mainValue={minutesToDisplay(stats?.totalWaitMinutes)}
            subText={
              !isAllComplex && stats?.avgWaitMinutes
                ? `배송 1회당 평균 ${minutesToDisplay(stats.avgWaitMinutes)} 소요`
                : null
            }
          />
        </div>

        <div className="dashboard-charts-wrapper">
          {/* ── 일 기준 운영 성과 ── */}
          <div className="dashboard-section">
            <div className="dashboard-section-title">일 기준 운영 성과</div>

            <ChartCard
              title="일별 배송 횟수"
              chartRef={dailyDeliveryRef}
              onReset={() => resetChartLegend(dailyDeliveryRef)}
              legendItems={LEGEND_DELIVERY_COUNT}
            >
              <div className="chart-container" style={{ height: 220 }}>
                {daily &&
                  (daily.deliveryChart.labels.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <Chart
                      ref={dailyDeliveryRef}
                      type="bar"
                      data={buildDeliveryCountData(
                        daily.deliveryChart.labels,
                        daily.deliveryChart.attemptCounts,
                        daily.deliveryChart.validCounts,
                        daily.deliveryChart.arrivalRates,
                      )}
                      options={buildDeliveryCountOptions()}
                    />
                  ))}
              </div>
            </ChartCard>

            <ChartCard
              title="일별 배송 시간"
              chartRef={dailyTimeRef}
              onReset={() => resetChartLegend(dailyTimeRef)}
              legendItems={LEGEND_STACKED_TIME}
            >
              <div className="chart-container" style={{ height: 220 }}>
                {daily &&
                  (daily.totalTimeChart.labels.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <Bar
                      ref={dailyTimeRef}
                      data={buildStackedBarData(
                        daily.totalTimeChart.labels,
                        daily.totalTimeChart.driveTotals,
                        daily.totalTimeChart.waitTotals,
                      )}
                      options={buildStackedBarOptions()}
                    />
                  ))}
              </div>
            </ChartCard>

            <ChartCard
              title="일별 배송 시간 상세"
              chartRef={dailyDetailRef}
              legendItems={LEGEND_DETAIL_TIME}
              staticLegend
            >
              <div className="chart-container" style={{ height: 220 }}>
                {daily &&
                  (daily.avgTimeChart.labels.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <Chart
                      ref={dailyDetailRef}
                      type="bar"
                      data={buildDetailTimeData(
                        daily.avgTimeChart.labels,
                        daily.avgTimeChart.driveAvgs,
                        daily.avgTimeChart.driveMaxs,
                        daily.avgTimeChart.driveMins,
                        daily.avgTimeChart.waitAvgs,
                        daily.avgTimeChart.waitMaxs,
                        daily.avgTimeChart.waitMins,
                      )}
                      options={buildDetailTimeOptions()}
                    />
                  ))}
              </div>
            </ChartCard>
          </div>

          {/* ── 요일 기준 운영 성과 ── */}
          <div className="dashboard-section">
            <div className="dashboard-section-title">요일 기준 운영 성과</div>

            <ChartCard
              title="요일별 배송 횟수"
              chartRef={dowDeliveryRef}
              onReset={() => resetChartLegend(dowDeliveryRef)}
              legendItems={LEGEND_DELIVERY_COUNT}
            >
              <div className="chart-container" style={{ height: 220 }}>
                {dow &&
                  (dow.deliveryChart.labels.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <Chart
                      ref={dowDeliveryRef}
                      type="bar"
                      data={buildDeliveryCountData(
                        dow.deliveryChart.labels,
                        dow.deliveryChart.attemptCounts,
                        dow.deliveryChart.validCounts,
                        dow.deliveryChart.arrivalRates,
                      )}
                      options={buildDeliveryCountOptions()}
                    />
                  ))}
              </div>
            </ChartCard>

            <div className="charts-row-2">
              <ChartCard
                title="요일별 배송 시간"
                chartRef={dowTimeRef}
                onReset={() => resetChartLegend(dowTimeRef)}
                legendItems={LEGEND_STACKED_TIME}
              >
                <div className="chart-container" style={{ height: 220 }}>
                  {dow &&
                    (dow.totalTimeChart.labels.length === 0 ? (
                      <EmptyChart />
                    ) : (
                      <Bar
                        ref={dowTimeRef}
                        data={buildStackedBarData(
                          dow.totalTimeChart.labels,
                          dow.totalTimeChart.driveTotals,
                          dow.totalTimeChart.waitTotals,
                        )}
                        options={buildStackedBarOptions()}
                      />
                    ))}
                </div>
              </ChartCard>

              <ChartCard
                title="요일별 배송 시간 상세"
                chartRef={dowDetailRef}
                legendItems={LEGEND_DETAIL_TIME}
                staticLegend
              >
                <div className="chart-container" style={{ height: 220 }}>
                  {dow &&
                    (dow.avgTimeChart.labels.length === 0 ? (
                      <EmptyChart />
                    ) : (
                      <Chart
                        ref={dowDetailRef}
                        type="bar"
                        data={buildDetailTimeData(
                          dow.avgTimeChart.labels,
                          dow.avgTimeChart.driveAvgs,
                          dow.avgTimeChart.driveMaxs,
                          dow.avgTimeChart.driveMins,
                          dow.avgTimeChart.waitAvgs,
                          dow.avgTimeChart.waitMaxs,
                          dow.avgTimeChart.waitMins,
                        )}
                        options={buildDetailTimeOptions()}
                      />
                    ))}
                </div>
              </ChartCard>
            </div>
          </div>

          {/* ── 시간대 기준 운영 성과 ── */}
          <div className="dashboard-section">
            <div className="dashboard-section-title">시간대 기준 운영 성과</div>
            <div className="chart-card">
              <div className="chart-title">시간대별 배송 시도 횟수</div>
              <div className="chart-container" style={{ height: 220 }}>
                {hourly &&
                  (hourly.labels.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <Bar
                      data={buildSimpleBarData(
                        hourly.labels,
                        hourly.attemptCounts,
                        "#009DAD",
                      )}
                      options={buildSimpleBarOptions(
                        (v) => `배송 시도 횟수: ${v}회`,
                      )}
                    />
                  ))}
              </div>
            </div>
          </div>

          {/* ── 목적지 기준 운영 성과 ── */}
          <div className="dashboard-section">
            <div
              style={{ display: "flex", alignItems: "baseline", gap: "12px" }}
            >
              <div className="dashboard-section-title">
                목적지 기준 운영 성과
              </div>
              <div className="filter-note">
                조회 기간 동안 로봇이 이동하지 않은 목적지는 차트에 표시되지
                않습니다.
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-title">목적지별 이동 횟수</div>
              {destination?.map((item) => (
                <DestCountItem
                  key={item.deviceId}
                  deviceName={item.deviceName}
                  data={item.data}
                  showTitle={isAllComplex}
                />
              ))}
            </div>

            <div className="chart-card">
              <div className="chart-title">목적지별 배송 시간</div>
              {destination?.map((item) => (
                <DestTotalTimeItem
                  key={item.deviceId}
                  deviceName={item.deviceName}
                  data={item.data}
                  showTitle={isAllComplex}
                />
              ))}
            </div>

            <div className="chart-card">
              <div className="chart-title">목적지별 배송 시간 상세</div>
              {destination?.map((item) => (
                <DestAvgTimeItem
                  key={item.deviceId}
                  deviceName={item.deviceName}
                  data={item.data}
                  showTitle={isAllComplex}
                />
              ))}
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default DashboardIndividual;
